import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { Stack, Row, Text, Button, Tag } from './index';
import { extractText } from '../lib/clientOcr';

export type KycContext = 'party' | 'entity' | 'friend' | 'party-prefill' | 'entity-prefill';

interface Props {
  context: KycContext;
  targetId?: string;        // required for party/entity/friend, omit for prefill modes
  dealId?: string;
  onAutofilled?: (fieldsAutofilled: string[]) => void;
  onPrefill?: (profile: any, artifactId: string, filename: string) => void;
}

const STATUS_COLOR: Record<string, string> = {
  green: 'bg-positive',
  yellow: 'bg-amber-400',
  red: 'bg-negative',
  pending: 'bg-muted',
  error: 'bg-negative',
};

const STATUS_LABEL: Record<string, string> = {
  green: 'Clean',
  yellow: 'PEP detected',
  red: 'Sanctions / risk flags',
  pending: 'KYC processing...',
  error: 'Review failed',
};

export function KycUploadCard({ context, targetId, dealId, onAutofilled, onPrefill }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [artifactId, setArtifactId] = useState<string | null>(null);
  const [filename, setFilename] = useState<string>('');
  const [reviewStatus, setReviewStatus] = useState<string | null>(null);
  const [reviewSummary, setReviewSummary] = useState<string | null>(null);
  const [entities, setEntities] = useState<any[]>([]);
  const [extractedProfile, setExtractedProfile] = useState<any>(null);
  const [extractionMethod, setExtractionMethod] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState<{ pct: number; label: string } | null>(null);
  const [autofilling, setAutofilling] = useState(false);
  const [autofillResult, setAutofillResult] = useState<{ fields: string[] } | null>(null);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [materializing, setMaterializing] = useState(false);
  const [matResult, setMatResult] = useState<{ created: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPrefillMode = context === 'party-prefill' || context === 'entity-prefill';

  useEffect(() => {
    if (!artifactId || reviewStatus !== 'pending') return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.artifactContent(artifactId);
        const art = r.artifact;
        if (art.reviewStatus && art.reviewStatus !== 'pending') {
          setReviewStatus(art.reviewStatus);
          setReviewSummary(art.reviewSummary ?? null);
          const f = art.reviewFindings;
          const findings = typeof f === 'string' ? JSON.parse(f) : f;
          setEntities(findings?.entities ?? []);
          setExtractionMethod(findings?.extractionMethod ?? null);
          if (isPrefillMode && findings?.entities?.length > 0) {
            // Find primary person or first entity
            const primary = findings.entities.find((e: any) => e.type === 'person') ?? findings.entities[0];
            setExtractedProfile(primary?.profile ?? null);
          }
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {}
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [artifactId, reviewStatus, isPrefillMode]);

  async function upload(file: File) {
    setUploading(true); setErr(null); setOcrProgress(null);
    console.log('[KYC OCR] file detected:', {
      name: file.name,
      type: file.type,
      size: `${(file.size / 1024).toFixed(1)} KB`,
    });

    try {
      // Step 1: HEIC check — unsupported, show error chip and bail
      const ext = file.name.toLowerCase().split('.').pop() ?? '';
      const mime = file.type ?? '';
      if (['heic', 'heif'].includes(ext) || mime === 'image/heic' || mime === 'image/heif') {
        setErr('iPhone HEIC images are not supported yet — please re-export as JPG or PNG and try again.');
        setUploading(false);
        setOcrProgress(null);
        return;
      }

      // Step 2: Client-side OCR for PDFs and images FIRST (before upload)
      // This ensures the transcript arrives at the server BEFORE review.run fires.
      const needsOcr = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'tiff', 'bmp'].includes(ext)
        || /^image\//.test(mime)
        || mime === 'application/pdf';
      let ocrResult: Awaited<ReturnType<typeof extractText>> | null = null;
      let ocrFailed = false;

      if (needsOcr) {
        console.log('[KYC OCR] starting extractText');
        try {
          ocrResult = await extractText(file, (pct, label) => {
            setOcrProgress({ pct, label });
          });
        } catch (ocrErr: any) {
          ocrFailed = true;
          console.error('[KYC OCR FAIL] extractText threw:', ocrErr);
          // Surface the failure but continue with upload — server-side text extraction is fallback
        }
      }
      setOcrProgress(null);

      // Step 3: Upload the original file
      console.log('[KYC OCR] uploading file…');
      const opts: any = { type: 'kyc', dealId };
      if (!isPrefillMode && targetId) {
        if (context === 'party') opts.partyId = targetId;
        else if (context === 'entity') opts.entityId = targetId;
        else if (context === 'friend') opts.friendId = targetId;
      }
      const r = await api.uploadDoc(file, opts);
      console.log('[KYC OCR] artifact created:', r.artifactId);
      setArtifactId(r.artifactId);
      setFilename(file.name);

      // Step 4: Post OCR transcript (queues review.run server-side)
      if (ocrResult && ocrResult.text && ocrResult.method !== 'unsupported') {
        console.log('[KYC OCR] posting transcript… method:', ocrResult.method, 'textLength:', ocrResult.text.length);
        try {
          await api.postOcrTranscript(r.artifactId, ocrResult.text, ocrResult.method);
          console.log('[KYC OCR] transcript saved, queueing review');
        } catch (postErr: any) {
          console.error('[KYC OCR FAIL] postOcrTranscript failed:', postErr);
          // Server will fall back to text extraction on its own
        }
      } else if (ocrFailed) {
        // OCR threw — mark status as pending, server-side extraction will run
        console.log('[KYC OCR] OCR failed in browser, server-side extraction will run');
      }

      // Step 5: Start polling for review completion
      console.log('[KYC OCR] polling artifact for review completion…');
      setReviewStatus('pending');
    } catch (e: any) {
      console.error('[KYC OCR FAIL] upload pipeline error:', e);
      setErr(e.message);
    } finally {
      setUploading(false);
      setOcrProgress(null);
    }
  }

  async function autofill() {
    if (!artifactId) return;
    setAutofilling(true); setErr(null);
    try {
      const r = await api.autofillProfileFromKyc(artifactId);
      setAutofillResult({ fields: r.fieldsAutofilled });
      if (onAutofilled) onAutofilled(r.fieldsAutofilled);
    } catch (e: any) { setErr(e.message); }
    finally { setAutofilling(false); }
  }

  function applyPrefill() {
    if (!extractedProfile || !onPrefill || !artifactId) return;
    onPrefill(extractedProfile, artifactId, filename);
    setPrefillApplied(true);
  }

  async function materializeRelated() {
    if (!artifactId) return;
    setMaterializing(true);
    try {
      const r = await api.materializeEntitiesFromKyc(artifactId);
      setMatResult({ created: r.created });
    } catch (e: any) { setErr(e.message); }
    finally { setMaterializing(false); }
  }

  // Not yet uploaded
  if (!artifactId) {
    return (
      <div className="hairline rounded-sm p-2.5 bg-whisper">
        <Stack gap={2}>
          <Text variant="micro">upload KYC first — autofill form fields</Text>
          <Text variant="label" muted className="text-[10px] leading-tight">
            Documents are OCR’d in your browser before upload. The original file is encrypted at rest.
            No third-party OCR service receives your documents.
          </Text>
          {ocrProgress ? (
            <Stack gap={1}>
              <div className="flex items-center justify-between">
                <Text variant="label" muted>{ocrProgress.label}</Text>
                <Text variant="label" muted>{ocrProgress.pct}%</Text>
              </div>
              <div className="h-1.5 bg-whisper rounded-full overflow-hidden hairline">
                <div
                  className="h-full bg-ink/70 rounded-full transition-all duration-300"
                  style={{ width: `${ocrProgress.pct}%` }}
                />
              </div>
            </Stack>
          ) : (
            <Row gap={1}>
              <Button variant="ghost" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? 'uploading...' : '↑ KYC document'}
              </Button>
              <input ref={fileRef} type="file" className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.docx,.doc,.txt"
                onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
            </Row>
          )}
          {err && <Text variant="label" className="text-negative">{err}</Text>}
        </Stack>
      </div>
    );
  }

  // Pending (also used while OCR is running before upload is queued)
  if (reviewStatus === 'pending') {
    return (
      <div className="hairline rounded-sm p-2.5 bg-whisper">
        <Stack gap={2}>
          <Row gap={2} align="center">
            <span className="h-2 w-2 rounded-full bg-muted animate-pulse shrink-0" />
            <Text variant="small" muted>
              {ocrProgress ? ocrProgress.label : 'KYC processing — 30-60 seconds'}
            </Text>
            {ocrProgress && <Text variant="label" muted>{ocrProgress.pct}%</Text>}
          </Row>
          {ocrProgress && (
            <div className="h-1.5 bg-whisper rounded-full overflow-hidden hairline">
              <div
                className="h-full bg-ink/70 rounded-full transition-all duration-300"
                style={{ width: `${ocrProgress.pct}%` }}
              />
            </div>
          )}
        </Stack>
      </div>
    );
  }

  const severityDot = STATUS_COLOR[reviewStatus ?? 'pending'];

  // Extraction method chip
  const ExtractionChip = () => {
    if (!extractionMethod || extractionMethod === 'claude-text') return null;
    const chipMap: Record<string, { label: string; cls: string }> = {
      // Client OCR paths
      'claude-text-from-tesseract-pdf':   { label: 'Scanned PDF — OCR’d in your browser', cls: 'bg-amber-100 text-amber-800' },
      'claude-text-from-tesseract-image': { label: 'Image OCR’d in your browser', cls: 'bg-amber-100 text-amber-800' },
      'claude-text-from-ocr':             { label: 'OCR’d in your browser', cls: 'bg-amber-100 text-amber-800' },
      // Text paths (no chip for plain text extraction)
      'pdf-text-layer':          { label: 'PDF text layer', cls: 'bg-positive/10 text-positive' },
      // Error states
      'manual-required':         { label: 'Could not extract — fill manually', cls: 'bg-negative/10 text-negative' },
      'ocr-browser-failed':      { label: 'OCR failed in browser — server extraction running', cls: 'bg-amber-100 text-amber-800' },
    };
    const chip = chipMap[extractionMethod];
    if (!chip) return null;
    return <span className={`text-xs px-1.5 py-0.5 rounded-sm font-medium ${chip.cls}`}>{chip.label}</span>;
  };

  // Reviewed — prefill mode
  if (isPrefillMode) {
    return (
      <div className="hairline rounded-sm p-2.5 bg-whisper">
        <Stack gap={2}>
          <Row gap={2} align="center">
            <span className={`h-2 w-2 rounded-full ${severityDot} shrink-0`} />
            <Text variant="small">{STATUS_LABEL[reviewStatus ?? 'pending']}</Text>
            {reviewSummary && <Text variant="label" muted>{reviewSummary}</Text>}
          </Row>
          <ExtractionChip />
          {entities.length > 0 && (
            <Stack gap={0.5}>
              {entities.slice(0, 3).map((e: any, i: number) => (
                <Row key={i} gap={1} align="center">
                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_COLOR[e.severity ?? 'green']}`} />
                  <Text variant="small">{e.name}</Text>
                  <Tag>{e.type}</Tag>
                  {e.profile?.pep && <Tag>PEP</Tag>}
                </Row>
              ))}
            </Stack>
          )}
          {err && <Text variant="label" className="text-negative">{err}</Text>}
          {prefillApplied ? (
            <Text variant="label" className="text-positive">
              ✓ Prefilled from {filename} — review and save
            </Text>
          ) : extractedProfile ? (
            <Button variant="primary" size="sm" onClick={applyPrefill}>
              Apply to form
            </Button>
          ) : (
            <Text variant="label" muted>no profile data extracted</Text>
          )}
        </Stack>
      </div>
    );
  }

  // Reviewed — standard autofill mode
  return (
    <div className="hairline rounded-sm p-2.5 bg-whisper">
      <Stack gap={2}>
        <Row gap={2} align="center">
          <span className={`h-2 w-2 rounded-full ${severityDot} shrink-0`} />
          <Text variant="small">{STATUS_LABEL[reviewStatus ?? 'pending']}</Text>
          {reviewSummary && <Text variant="label" muted>{reviewSummary}</Text>}
        </Row>
        <ExtractionChip />
        {entities.length > 0 && (
          <Stack gap={0.5}>
            {entities.slice(0, 4).map((e: any, i: number) => (
              <Row key={i} gap={1} align="center">
                <span className={`h-1.5 w-1.5 rounded-full ${STATUS_COLOR[e.severity ?? 'green']}`} />
                <Text variant="small">{e.name}</Text>
                <Tag>{e.type}</Tag>
                {e.profile?.pep && <Tag>PEP</Tag>}
              </Row>
            ))}
          </Stack>
        )}
        {err && <Text variant="label" className="text-negative">{err}</Text>}
        {autofillResult ? (
          <Row gap={2} align="center">
            <span className="text-positive text-sm">✓ Profile autofilled</span>
            <Text variant="label">{autofillResult.fields.length} field{autofillResult.fields.length !== 1 ? 's' : ''} populated</Text>
          </Row>
        ) : (
          <Row gap={1}>
            <Button variant="primary" size="sm" disabled={autofilling} onClick={autofill}>
              {autofilling ? 'autofilling...' : 'Add details to profile'}
            </Button>
            {entities.length > 1 && !matResult && (
              <Button variant="secondary" size="sm" disabled={materializing} onClick={materializeRelated}>
                {materializing ? 'creating...' : 'Create CRM profiles'}
              </Button>
            )}
            {matResult && <Text variant="label" className="text-positive">{matResult.created} CRM profiles created</Text>}
          </Row>
        )}
      </Stack>
    </div>
  );
}

